// src/auth/authClient.js
import { AUTH_ENABLED } from './flags';

// Read env (CRA only exposes REACT_APP_* at build time)
const cfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
};

function hasFirebaseConfig(c) {
  return !!(c?.apiKey && c?.authDomain && c?.projectId && c?.appId && c?.messagingSenderId);
}

// ---- MOCK MODE (used until real keys are provided) ----
const mock = {
  mode: 'mock',
  getUser: () => null,
  onChange: (cb) => { cb(null); return () => {}; },
  signInWithGoogle: async () => { throw new Error('Firebase not configured'); },
  signInWithApple: async () => { throw new Error('Firebase not configured'); },
  signOut: async () => {},
  getIdToken: async () => null,
};

// ---- REAL FIREBASE MODE (only if keys exist) ----
let firebaseClient = null;
let firebaseInitPromise = null;
let firebaseInitError = null;
let pendingCallbacks = [];

async function initFirebaseClient() {
  if (firebaseClient) return firebaseClient;
  if (firebaseInitError) throw firebaseInitError;

  try {
    const { initializeApp } = await import('firebase/app');
    const { getAuth, setPersistence, browserLocalPersistence, GoogleAuthProvider,
            signInWithPopup, onAuthStateChanged, signOut } = await import('firebase/auth');

    const app = initializeApp(cfg);
    const auth = getAuth(app);

    // Safari can fail on setPersistence in private browsing - wrap in try-catch
    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch (persistError) {
      console.warn('Firebase persistence setup failed (possibly private browsing):', persistError);
      // Continue anyway - Firebase will use in-memory persistence
    }

    firebaseClient = {
      mode: 'firebase',
      auth,
      GoogleAuthProvider,
      signInWithPopup,
      onAuthStateChanged,
      signOutFn: signOut,
    };

    // Notify any callbacks that were registered before init completed
    pendingCallbacks.forEach(({ callback, unsub }) => {
      if (!unsub.cancelled) {
        try {
          const realUnsub = onAuthStateChanged(auth, callback);
          unsub.real = realUnsub;
        } catch (e) {
          console.warn('Failed to register auth callback:', e);
        }
      }
    });
    pendingCallbacks = [];

    return firebaseClient;
  } catch (e) {
    console.error('Firebase initialization failed:', e);
    firebaseInitError = e;
    firebaseClient = null;

    // Notify pending callbacks with null user
    pendingCallbacks.forEach(({ callback }) => {
      try {
        callback(null);
      } catch (cbError) {
        console.warn('Auth callback error:', cbError);
      }
    });
    pendingCallbacks = [];

    throw e;
  }
}

// Safely initialize Firebase without leaving unhandled rejections
function ensureFirebaseInit() {
  if (!firebaseInitPromise) {
    firebaseInitPromise = initFirebaseClient().catch((e) => {
      // Error is already logged and stored in firebaseInitError
      // Return null to prevent unhandled rejection
      return null;
    });
  }
  return firebaseInitPromise;
}

// Create a client interface that handles async initialization gracefully
function createAuthInterface() {
  return {
    mode: hasFirebaseConfig(cfg) ? 'firebase' : 'mock',

    getUser: () => {
      if (firebaseClient?.auth) {
        return firebaseClient.auth.currentUser;
      }
      return null;
    },

    // onChange must return an unsubscribe function synchronously
    // We handle the async init by queuing callbacks
    onChange: (callback) => {
      // If already initialized, use Firebase directly
      if (firebaseClient?.auth) {
        try {
          return firebaseClient.onAuthStateChanged(firebaseClient.auth, callback);
        } catch (e) {
          console.warn('Failed to attach auth listener:', e);
          callback(null);
          return () => {};
        }
      }

      // If init already failed, just call with null
      if (firebaseInitError) {
        callback(null);
        return () => {};
      }

      // If not using Firebase config, use mock
      if (!hasFirebaseConfig(cfg) || !AUTH_ENABLED) {
        callback(null);
        return () => {};
      }

      // Create a wrapper unsubscribe that will be updated once init completes
      const unsub = { real: null, cancelled: false };

      // Queue this callback to be registered once Firebase inits
      pendingCallbacks.push({ callback, unsub });

      // Start initialization if not already started
      ensureFirebaseInit();

      // Return unsubscribe function that works before and after init
      return () => {
        unsub.cancelled = true;
        if (unsub.real) {
          try {
            unsub.real();
          } catch (e) {
            // Ignore unsubscribe errors
          }
        } else {
          // Remove from pending if not yet initialized
          const idx = pendingCallbacks.findIndex(p => p.unsub === unsub);
          if (idx !== -1) pendingCallbacks.splice(idx, 1);
        }
      };
    },

    signInWithGoogle: async () => {
      if (!hasFirebaseConfig(cfg) || !AUTH_ENABLED) {
        throw new Error('Firebase not configured');
      }

      await ensureFirebaseInit();

      if (!firebaseClient) {
        throw new Error('Firebase not available');
      }

      return firebaseClient.signInWithPopup(firebaseClient.auth, new firebaseClient.GoogleAuthProvider());
    },

    signInWithApple: async () => {
      if (!hasFirebaseConfig(cfg) || !AUTH_ENABLED) {
        throw new Error('Firebase not configured');
      }

      await ensureFirebaseInit();

      if (!firebaseClient) {
        throw new Error('Firebase not available');
      }

      // Import OAuthProvider for Apple Sign-In
      const { OAuthProvider } = await import('firebase/auth');
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      return firebaseClient.signInWithPopup(firebaseClient.auth, provider);
    },

    signOut: async () => {
      if (!firebaseClient?.auth) return;
      try {
        return await firebaseClient.signOutFn(firebaseClient.auth);
      } catch (e) {
        console.warn('Sign out error:', e);
      }
    },

    getIdToken: async () => {
      // If Firebase isn't ready yet, try to initialize
      if (!firebaseClient?.auth) {
        if (!hasFirebaseConfig(cfg) || !AUTH_ENABLED) {
          return null;
        }

        await ensureFirebaseInit();

        // If still no client after init attempt, return null
        if (!firebaseClient?.auth) {
          return null;
        }
      }

      const user = firebaseClient.auth.currentUser;
      if (!user) return null;

      try {
        return await user.getIdToken();
      } catch (e) {
        // Token refresh can fail for various reasons (offline, session expired, etc.)
        // Log but don't throw - caller will handle null token appropriately
        console.warn('Failed to get ID token:', e?.message || e);
        return null;
      }
    },
  };
}

let authInterface = null;

export function getAuthClient() {
  if (!AUTH_ENABLED) return mock;
  if (!hasFirebaseConfig(cfg)) return mock;

  if (!authInterface) {
    authInterface = createAuthInterface();
  }

  return authInterface;
}
